import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const contactMethods = [
  "Email",
  "Phone",
  "Text"
];

const contactTimes = [
  "Morning",
  "Afternoon",
  "Evening",
  "Any Time"
];

const ContactInfoStep = ({ onNext, initialValue = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  preferredContact: "",
  preferredTime: ""
} }) => {
  const [formData, setFormData] = useState(initialValue);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    if (!formData.preferredContact) {
      newErrors.preferredContact = 'Please select how you would like to be contacted';
    }
    if (!formData.preferredTime) {
      newErrors.preferredTime = 'Please select your preferred contact time';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext(formData);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col min-h-[400px] md:min-h-[450px]"
    >
      <div className="space-y-3 mb-6">
        <div className="text-center space-y-1">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-900"
          >
            How should we reach you?
          </motion.h2>
          <div className="w-12 h-0.5 bg-[#1a4a1a] mx-auto"></div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-500 text-sm md:text-base mt-2"
          >
            We will only share your contact information if you're matched with one of our lending partners
          </motion.p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-grow space-y-4 max-w-md mx-auto">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-base text-gray-700">First Name</Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
              placeholder="First name"
              className={`w-full text-base md:text-lg py-6 ${errors.firstName ? 'border-red-500' : ''}`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-sm">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-base text-gray-700">Last Name</Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => handleChange('lastName', e.target.value)}
              placeholder="Last name"
              className={`w-full text-base md:text-lg py-6 ${errors.lastName ? 'border-red-500' : ''}`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-sm">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-base text-gray-700">Email Address</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="email@example.com"
            className={`w-full text-base md:text-lg py-6 ${errors.email ? 'border-red-500' : ''}`}
          />
          {errors.email && (
            <p className="text-red-500 text-sm">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-base text-gray-700">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(123) 456-7890"
            className={`w-full text-base md:text-lg py-6 ${errors.phone ? 'border-red-500' : ''}`}
          />
          {errors.phone && (
            <p className="text-red-500 text-sm">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="preferredContact" className="text-base text-gray-700">How would you like to be contacted?</Label>
          <Select
            value={formData.preferredContact}
            onValueChange={(value) => handleChange('preferredContact', value)}
          >
            <SelectTrigger id="preferredContact" className={`w-full text-base md:text-lg py-6 ${errors.preferredContact ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select contact method" />
            </SelectTrigger>
            <SelectContent>
              {contactMethods.map((method) => (
                <SelectItem
                  key={method}
                  value={method}
                  className="text-base cursor-pointer py-3 px-4"
                >
                  {method}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.preferredContact && (
            <p className="text-red-500 text-sm">{errors.preferredContact}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="preferredTime" className="text-base text-gray-700">What time do you prefer to be contacted?</Label>
          <Select
            value={formData.preferredTime}
            onValueChange={(value) => handleChange('preferredTime', value)}
          >
            <SelectTrigger id="preferredTime" className={`w-full text-base md:text-lg py-6 ${errors.preferredTime ? 'border-red-500' : ''}`}>
              <SelectValue placeholder="Select preferred time" />
            </SelectTrigger>
            <SelectContent>
              {contactTimes.map((time) => (
                <SelectItem
                  key={time}
                  value={time}
                  className="text-base cursor-pointer py-3 px-4"
                >
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.preferredTime && (
            <p className="text-red-500 text-sm">{errors.preferredTime}</p>
          )}
        </div>
      </form>

      <div className="mt-6">
        <Button
          onClick={handleSubmit}
          className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-3 md:py-4 text-base md:text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          Continue
        </Button>
      </div>
    </motion.div>
  );
};

export default ContactInfoStep; 